import { copy } from '@ohear/shared'
/**
 * Few-takes soft nudge — Pro Tools light panel.
 *
 * Fires when the actor presses N (advance) but only has 1 or 2 takes recorded
 * for the active cue. ADR-industry convention is 3 takes per line; this is a
 * gentle, one-time-per-cue reminder, never a blocker. Once the actor has 3+
 * takes on a cue we trust they hit the convention and don't show the prompt
 * at all.
 *
 * Three actions (rendered in the parent Modal's footer):
 *   - Skip this cue       — closes prompt, host advances to the next cue
 *   - Record another      — closes prompt, host kicks off a fresh take (T+1)
 *   - Keep what I have    — closes prompt, host advances; actor stands by
 *                            their existing take(s)
 *
 * Tone rules: phrased as a question, no warning red, one issue at a time.
 */
import { Button, Modal } from '@ohear/ui'
import { useT } from '../../lib/i18n/use-t'

export interface ThreeTakePromptProps {
  open: boolean
  /** Number of takes the actor has recorded for this cue (1 or 2 in practice). */
  takeCount: number
  /** "I'm fine, advance" — accept current take count and move on. */
  onPickBest: () => void
  /** "Record another" — start a fresh take on this cue. */
  onRecordFourth: () => void
  /** "Skip" — advance without recording more. */
  onSkipCue: () => void
  onClose: () => void
}

export function ThreeTakePrompt({
  open,
  takeCount,
  onPickBest,
  onRecordFourth,
  onSkipCue,
  onClose,
}: ThreeTakePromptProps) {
  const t = useT()
  // Body copy adapts to "1 take" vs "2 takes" so the actor sees the actual
  // number, not a generic "you have a few takes" string.
  const bodyMessage =
    takeCount <= 1
      ? {
          zh: '本条只录了 1 条 — 要不要再来一遍?三条是行业惯例,不强制。',
          en: 'You only recorded 1 take on this line — record another? Three is the industry default, but not required.',
          es: 'Solo grabaste 1 toma — ¿quieres hacer otra? Tres es lo habitual, pero no obligatorio.',
        }
      : {
          zh: '本条有 2 条 take — 要不要再录第三条?三条是行业惯例,不强制。',
          en: 'You have 2 takes on this line — record a third? Three is the industry default, but not required.',
          es: 'Tienes 2 tomas — ¿grabar una tercera? Tres es lo habitual, pero no obligatorio.',
        }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t(copy.length.THREE_TAKE_TITLE)}
      footer={
        <>
          <Button variant="ghost" onClick={onSkipCue}>
            <span aria-hidden="true">{copy.length.SKIP_CUE.icon} </span>
            {t(copy.length.SKIP_CUE)}
          </Button>
          <Button variant="ghost" onClick={onRecordFourth}>
            <span aria-hidden="true">{copy.length.RECORD_FOURTH.icon} </span>
            {t(copy.length.RECORD_FOURTH)}
          </Button>
          <Button variant="primary" onClick={onPickBest}>
            <span aria-hidden="true">{copy.length.PICK_BEST.icon} </span>
            {t(copy.length.PICK_BEST)}
          </Button>
        </>
      }
    >
      <div
        data-testid="three-take-prompt-body"
        className="rounded-sm border border-pt-border bg-pt-panel2 p-3 text-[13px] text-pt-text"
      >
        <span aria-hidden="true">{copy.length.THREE_TAKE_TITLE.icon} </span>
        {t(bodyMessage)}
      </div>
    </Modal>
  )
}
